// Инвентарь и выбор оружия локального игрока + предсказание спуска.
//
// Сервер — источник истины: в хвосте снапшота приходят маска оружия и
// патроны по видам (yourOwned/yourAmmo), в записи игрока — активное оружие.
// Клиент:
//   - сразу переключает оружие в руках по клавише (предсказание), шлёт выбор
//     в битах 12..15 Input.buttons, пока сервер не подтвердит;
//   - сам решает, когда «нажался спуск» (темп, очередь, патроны) — для
//     мгновенных звука/вспышки/отдачи; урон считает только сервер.

import {
  AUTO_SWITCH_ORDER, WEAPON_COUNT, WEAPON_SELECT_SHIFT, WEAPON_SWITCH_TICKS,
  WeaponId, weaponDef,
} from "./catalog";

/** Сколько ждём подтверждения выбора от сервера, прежде чем сдаться. */
const SELECT_CONFIRM_TIMEOUT_MS = 1500;

export type FireResult = "fire" | "dry" | null;

export class WeaponState {
  owned = (1 << WeaponId.Fists) | (1 << WeaponId.Rifle);
  ammo: number[] = [-1, 0, 0, 50, 0, 0, 0, 0, 0];
  /** Оружие в руках (предсказанное: сразу после нажатия клавиши). */
  active: number = WeaponId.Rifle;
  /** Последнее подтверждённое сервером. */
  serverActive: number = WeaponId.Rifle;
  /** Предыдущее оружие (Q — быстрый возврат). */
  previous: number = WeaponId.Fists;
  /** Тики до готовности к выстрелу (темп + подъём после смены). */
  cooldownTicks = 0;
  /** Время последней смены оружия (для анимации подъёма). */
  switchedAtMs = 0;
  private pendingSinceMs = 0;
  /** Снимок пришёл хотя бы раз (до этого HUD не врёт стартовыми числами). */
  synced = false;

  owns(id: number): boolean {
    return id >= 0 && id < WEAPON_COUNT && (this.owned & (1 << id)) !== 0;
  }

  ammoOf(id: number): number {
    return this.ammo[id] ?? 0;
  }

  /** Есть чем стрелять (бесконечно или > 0). */
  hasAmmo(id: number): boolean {
    const a = this.ammoOf(id);
    return a < 0 || a > 0;
  }

  /** Выбор оружия игроком. false — нет такого оружия или уже в руках. */
  select(id: number, nowMs: number): boolean {
    if (!this.owns(id) || id === this.active) return false;
    this.previous = this.active;
    this.active = id;
    this.switchedAtMs = nowMs;
    this.pendingSinceMs = nowMs;
    if (this.cooldownTicks < WEAPON_SWITCH_TICKS) this.cooldownTicks = WEAPON_SWITCH_TICKS;
    return true;
  }

  /** Следующее/предыдущее имеющееся оружие (колесо мыши, тач-кнопка). */
  cycle(dir: 1 | -1, nowMs: number): boolean {
    for (let k = 1; k <= WEAPON_COUNT; k++) {
      const id = (this.active + dir * k + WEAPON_COUNT * 2) % WEAPON_COUNT;
      if (this.owns(id)) return this.select(id, nowMs);
    }
    return false;
  }

  selectPrevious(nowMs: number): boolean {
    return this.select(this.previous, nowMs);
  }

  /** Лучшее оружие с патронами (автосмена после сухого щелчка). */
  bestWithAmmo(): number {
    for (const id of AUTO_SWITCH_ORDER) {
      if (id !== this.active && this.owns(id) && this.hasAmmo(id)) return id;
    }
    return -1;
  }

  /** Биты выбора для Input.buttons — шлём, пока сервер не подтвердил. */
  selectBits(): number {
    if (this.pendingSinceMs === 0) return 0;
    return ((this.active + 1) & 0xF) << WEAPON_SELECT_SHIFT;
  }

  /** Снимок сервера: инвентарь и оружие в руках. */
  onSnapshot(owned: number, ammo: number[], serverActive: number, nowMs: number): void {
    this.owned = owned;
    for (let i = 0; i < WEAPON_COUNT; i++) this.ammo[i] = ammo[i] ?? 0;
    const changedOnServer = serverActive !== this.serverActive;
    this.serverActive = serverActive;
    this.synced = true;
    if (this.pendingSinceMs !== 0) {
      if (serverActive === this.active) {
        this.pendingSinceMs = 0;
      } else if (nowMs - this.pendingSinceMs > SELECT_CONFIRM_TIMEOUT_MS || !this.owns(this.active)) {
        // Сервер не принял (нет оружия) — возвращаемся к его выбору.
        this.pendingSinceMs = 0;
        this.adoptServer(serverActive, nowMs);
      }
      return;
    }
    if (this.active !== serverActive) {
      // Сервер сам сменил оружие (подобрали новое, респаун).
      this.adoptServer(serverActive, nowMs);
    } else if (changedOnServer) {
      this.switchedAtMs = nowMs;
    }
  }

  private adoptServer(id: number, nowMs: number): void {
    if (id !== this.active) {
      this.previous = this.active;
      this.active = id;
      this.switchedAtMs = nowMs;
    }
  }

  /** Сброс на старте/респауне (до первого снапшота). */
  reset(): void {
    this.owned = (1 << WeaponId.Fists) | (1 << WeaponId.Rifle);
    this.ammo = [-1, 0, 0, 50, 0, 0, 0, 0, 0];
    this.active = WeaponId.Rifle;
    this.serverActive = WeaponId.Rifle;
    this.previous = WeaponId.Fists;
    this.cooldownTicks = 0;
    this.pendingSinceMs = 0;
    this.synced = false;
  }

  /** Фикс-тик: решить, нажался ли спуск (как сервер в weaponInput). */
  tickFire(fireHeld: boolean, firePrev: boolean): FireResult {
    if (this.cooldownTicks > 0) this.cooldownTicks--;
    if (!fireHeld) return null;
    const def = weaponDef(this.active);
    if (firePrev && !def.auto) return null;
    if (this.cooldownTicks > 0) return null;
    this.cooldownTicks = def.cooldownTicks;
    const a = this.ammoOf(this.active);
    if (a === 0) return "dry";
    if (a > 0) this.ammo[this.active] = a - 1;
    return "fire";
  }
}
